import React, { useState } from 'react';
import { Message, MESSAGE_SENDERS, createMessage } from '../types/Message';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import { CitationDisplay } from './CitationDisplay';
import { useKnowledgeBase } from '../hooks/useKnowledgeBase';
import { useAuth } from '../contexts/AuthContext';
import { appConfig } from '../config/env';

const ChatInterface: React.FC = () => {
  // useState: メッセージ一覧を管理するState
  const [messages, setMessages] = useState<Message[]>([]);
  
  // 認証状態を取得
  const { isAuthenticated, hasValidToken, userEmail, idToken } = useAuth();
  
  // 統合されたKnowledge Baseフックを使用（Cognito認証対応）
  const { 
    loading: isLoading, 
    error, 
    session, 
    lastResponse, 
    citations,
    progress,
    currentStatus,
    requestId,
    sendMessageWithCitations,
    clearError,
    cancelQuery
  } = useKnowledgeBase();

  // メッセージ送信処理
  const handleSendMessage = async (content: string) => {
    // エラーをクリア
    clearError();
    
    // ユーザーメッセージを追加（ヘルパー関数を使用）
    const userMessage = createMessage(content, MESSAGE_SENDERS.USER);

    // メッセージ一覧に追加（既存のメッセージに新しいメッセージを追加）
    setMessages(prevMessages => [...prevMessages, userMessage]);

    try {
      // Knowledge Base APIを呼び出し（Cognito認証対応）
      const response = await sendMessageWithCitations(content, idToken || undefined);
      
      // アシスタントメッセージを作成（引用情報も含める）
      const assistantMessage: Message = {
        ...createMessage(response.generated_response, MESSAGE_SENDERS.ASSISTANT),
        // 追加のメタデータとして引用情報を保存
        metadata: {
          citations: response.citations,
          sessionId: response.session_id,
          knowledgeBaseId: response.knowledge_base_id,
          modelArn: response.model_arn,
          requestId: requestId,
          authMethod: 'Cognito'
        }
      };
      
      setMessages(prevMessages => [...prevMessages, assistantMessage]);
    } catch (error) {
      console.error('メッセージ送信エラー:', error);
      
      let errorMessageText = 'エラーが発生しました。もう一度お試しください。';
      
      // Cognito認証エラーの場合の特別処理
      if (error instanceof Error) {
        if (error.message.includes('Cognito ID Token is required')) {
          errorMessageText = 'ログインが必要です。右上のログインボタンからサインインしてください。';
        } else if (error.message.includes('Unauthorized')) {
          errorMessageText = '認証が無効です。再度ログインしてください。';
        } else if (error.message.includes('403')) {
          errorMessageText = 'アクセス権限がありません。管理者にお問い合わせください。';
        }
      }
      
      const errorMessage = createMessage(errorMessageText, MESSAGE_SENDERS.ASSISTANT);
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    }
  };

  // キャンセル処理
  const handleCancel = () => {
    cancelQuery();
  };

  // 認証状態の表示
  const renderAuthInfo = () => {
    if (!appConfig.debugMode) return null;
    
    return (
      <div className="auth-info-debug">
        <small>
          ユーザー: {userEmail || '未ログイン'} |
          認証状態: {isAuthenticated ? 'ログイン済み' : '未ログイン'}
        </small>
      </div>
    );
  };

  // 認証が必要な場合の表示
  const renderAuthRequired = () => {
    if (isAuthenticated && hasValidToken) return null;
    
    return (
      <div className="auth-required">
        <div className="auth-required-content">
          <h3>🔐 ログインが必要です</h3>
          <p>
            このアプリケーションを使用するには、ログインが必要です。
            右上の「ログイン」ボタンからサインインしてください。
          </p>
          <div className="auth-features">
            <h4>ログイン後の機能:</h4>
            <ul>
              <li>Knowledge Base APIへのアクセス</li>
              <li>セッション管理による会話の継続</li>
              <li>引用情報付きの詳細な回答</li>
              <li>非同期処理による長時間クエリ対応</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // 進捗情報の表示
  const renderProgressInfo = () => {
    if (!isLoading && !progress) return null;
    
    return (
      <div className="progress-info">
        <div className="progress-status">
          <span className="progress-text">{progress}</span>
          {currentStatus && (
            <span className="progress-badge" data-status={currentStatus.toLowerCase()}>
              {currentStatus}
            </span>
          )}
        </div>
        {requestId && (
          <div className="progress-details">
            <small>Request ID: {requestId}</small>
          </div>
        )}
        {isLoading && (
          <button onClick={handleCancel} className="cancel-button">
            キャンセル
          </button>
        )}
      </div>
    );
  };

  // セッション情報の表示（デバッグ用）
  const renderSessionInfo = () => {
    if (!appConfig.debugMode) return null;
    
    return (
      <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '8px' }}>
        <div>Debug Mode | Mock API: {appConfig.useMockApi ? 'ON' : 'OFF'}</div>
        <div>認証状態: {isAuthenticated ? 'ログイン済み' : '未ログイン'}</div>
        <div>有効なトークン: {hasValidToken ? 'あり' : 'なし'}</div>
        <div>Session ID: {session.sessionId || 'New Session'}</div>
        <div>Message History: {session.messageHistory.length} messages</div>
        <div>Request ID: {requestId || 'None'}</div>
        <div>Current Status: {currentStatus || 'Idle'}</div>
        {lastResponse && (
          <div>Last KB ID: {lastResponse.knowledge_base_id}</div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* 認証・進捗表示用の追加スタイル */}
      <style>{`
        .auth-info-debug {
          background: #e3f2fd;
          border: 1px solid #bbdefb;
          border-radius: 4px;
          padding: 8px;
          margin: 8px 0;
          text-align: center;
          color: #1976d2;
        }
        
        .auth-required {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          border-radius: 8px;
          padding: 20px;
          margin: 20px 0;
          text-align: center;
        }
        
        .auth-required-content h3 {
          color: #856404;
          margin-bottom: 12px;
        }
        
        .auth-required-content p {
          color: #856404;
          margin-bottom: 16px;
        }
        
        .auth-features {
          text-align: left;
          max-width: 400px;
          margin: 0 auto;
        }
        
        .auth-features h4 {
          color: #856404;
          margin-bottom: 8px;
        }
        
        .auth-features ul {
          color: #856404;
          padding-left: 20px;
        }
        
        .auth-features li {
          margin-bottom: 4px;
        }
        
        .progress-info {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 12px;
          margin: 8px 0;
          font-size: 0.9rem;
        }
        
        .progress-status {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
        }
        
        .progress-text {
          font-weight: 500;
        }
        
        .progress-badge {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }
        
        .progress-badge[data-status="queued"] {
          background: #fff3cd;
          color: #856404;
        }
        
        .progress-badge[data-status="processing"] {
          background: #cce5ff;
          color: #004085;
        }
        
        .progress-badge[data-status="completed"] {
          background: #d4edda;
          color: #155724;
        }
        
        .progress-badge[data-status="failed"] {
          background: #f8d7da;
          color: #721c24;
        }
        
        .progress-details {
          color: #6c757d;
          font-size: 0.8rem;
        }
        
        .cancel-button {
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 4px 12px;
          font-size: 0.8rem;
          cursor: pointer;
          margin-top: 8px;
        }
        
        .cancel-button:hover {
          background: #c82333;
        }
        
        .loading-indicator {
          background: #e3f2fd;
          border-left: 4px solid #2196f3;
          padding: 12px;
          margin: 8px 0;
          border-radius: 4px;
        }
        
        .loading-indicator p {
          margin: 0 0 4px 0;
          font-weight: 500;
          color: #1976d2;
        }
        
        .loading-details {
          color: #666;
          font-size: 0.85rem;
        }
      `}</style>
      
      <div className="chat-interface">
        <div className="chat-header">
          <h1>{appConfig.appName}</h1>
          <div className="header-subtitle">
            AI搭載 Knowledge Base チャット
          </div>
          {renderAuthInfo()}
          {renderSessionInfo()}
        </div>
        
        <div className="chat-messages">
          {/* 認証が必要な場合の表示 */}
          {renderAuthRequired()}
          
          {/* 認証済みの場合のみメッセージ表示 */}
          {(isAuthenticated && hasValidToken) && (
            <>
              <MessageList 
                messages={messages} 
                showCitations={true}
              />
              
              {/* 進捗情報表示 */}
              {renderProgressInfo()}
              
              {isLoading && (
                <div className="loading-indicator">
                  <p>AIが情報を検索・分析中...</p>
                  <div className="loading-details">
                    <small>詳細な回答を生成しています。しばらくお待ちください。</small>
                  </div>
                </div>
              )}
              {error && (
                <div className="error-indicator">
                  <p>⚠️ エラー: {error}</p>
                  <button onClick={clearError} className="error-dismiss">
                    ✕ 閉じる
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="chat-input">
          <MessageInput 
            onSendMessage={handleSendMessage} 
            disabled={isLoading || !isAuthenticated || !hasValidToken}
            placeholder={
              (isAuthenticated && hasValidToken)
                ? "何でもお聞きください..." 
                : "ログインしてからご利用ください..."
            }
          />
        </div>
        
        {/* 最新の引用情報を表示（デバッグ用） */}
        {appConfig.debugMode && citations.length > 0 && (
          <div className="debug-citations">
            <h3>最新の引用情報 (Debug)</h3>
            <CitationDisplay citations={lastResponse?.citations || []} />
          </div>
        )}
      </div>
    </>
  );
};

export default ChatInterface;
